import React, { useState, useEffect } from "react";
import { Slider, Radio, Tree, Typography, Avatar, Alert } from "antd";
import { Agent } from "./types";
import { getNameAbbrevation } from "./service/service";

interface AgentsTreeProps {
  agent: Agent | null;
}

const getColorByDepth = (depth: number): string => {
  const hue = (depth * 137.5) % 360;
  return `hsl(${hue}, 70%, 50%)`;
};

const { TreeNode } = Tree;

const AgentsTree: React.FC<AgentsTreeProps> = ({ agent }) => {
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [sliderValue, setSliderValue] = useState<number>(3);
  const [visibility, setVisibility] = useState<string>("expand");

  useEffect(() => {
    // Expand all nodes by default
    if (agent) {
      setExpandedKeys(getExpandedKeys(100)); // Assuming 100 is the maximum depth
    }
  }, [agent]);

  const handleChange = (value: number) => {
    setSliderValue(value);
    // Expand tree based on slider value
    setExpandedKeys(getExpandedKeys(value));
  };

  const handleVisibilityChange = (e: any) => {
    const value = e.target.value;
    setVisibility(value);
    if (value === "collapse") {
      setExpandedKeys([]); // Collapse all nodes
    } else {
      setExpandedKeys(getExpandedKeys(sliderValue)); // Expand nodes based on slider value
    }
  };

  const getExpandedKeys = (value: number) => {
    const keys: string[] = [];
    if (agent) {
      const queue: { agent: Agent; level: number }[] = [{ agent, level: 0 }];
      while (queue.length > 0) {
        const { agent, level } = queue.shift()!;
        if (level <= value) {
          keys.push(agent.id);
          if (agent.agent_referrals) {
            agent.agent_referrals.forEach((child) =>
              queue.push({ agent: child, level: level + 1 })
            );
          }
        }
      }
    }
    return keys;
  };

  const transformAgentToTreeData = (agent: Agent, depth: number = 0): any => ({
    title: (
      <div style={{ display: "flex", alignItems: "center" }}>
        <Avatar style={{ height: "25px", width: "25px" }}>
          {getNameAbbrevation(agent.first_name, agent.last_name)}
        </Avatar>
        <div
          style={{
            fontSize: "15px",
            padding: "2px 5px",
            color: getColorByDepth(depth),
          }}
        >
          {`${agent.first_name} ${agent.last_name} `}
        </div>
      </div>
    ),
    key: agent.id,
    children: agent.agent_referrals
      ? agent.agent_referrals.map((child) =>
          transformAgentToTreeData(child, depth + 1)
        )
      : [],
  });

  const treeData = agent ? [transformAgentToTreeData(agent)] : [];

  const marks = {
    0: "0",
    1: "1",
    2: "2",
    3: "3",
    4: "4",
    5: "5",
    6: "6",
    7: "7",
    8: "8",
    9: "9",
    10: "10",
  };

  return (
    <div>
      <div style={{ padding: "5px 20px" }}>
        <h3>Hierarchical View of the Network</h3>
        <p>View the complete network in a hierarchical view</p>
        <Alert
          message="Please note, based on the filter and search criteria the nodes in the tree will be visible."
          type="warning"
          showIcon
          closable
        />

        <div style={{ padding: "10px 0px" }}></div>
        <Slider
          min={0}
          max={10}
          step={1}
          value={sliderValue}
          onChange={handleChange}
          marks={marks}
        />
        <div style={{ padding: "20px 0px" }}>
          <Radio.Group
            defaultValue={visibility}
            onChange={handleVisibilityChange}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="expand">Expand</Radio.Button>
            <Radio.Button value="collapse">Collapse</Radio.Button>
          </Radio.Group>
        </div>
      </div>

      <Tree
        className="agent-tree-container"
        showLine
        treeData={treeData}
        expandedKeys={expandedKeys}
        onExpand={(keys) => setExpandedKeys(keys as string[])}
      />
    </div>
  );
};

export default AgentsTree;
